import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/app/PageHeader';
import { DataTable } from '@/components/app/DataTable';
import { ErrorState } from '@/components/app/ErrorState';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { humanizeError } from '@/lib/apiClient';
import {
  funnelObservabilityApi,
  type CoverageGap,
  type CoverageGapStatus,
} from '../api/funnelObservabilityApi';

type StatusFilter = '' | CoverageGapStatus;

const STATUS_FILTERS: Array<{ label: string; value: StatusFilter }> = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'open' },
  { label: 'Triaged', value: 'triaged' },
  { label: 'Proposed', value: 'taxonomy_extension_proposed' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Dismissed', value: 'dismissed' },
];

function StatusBadge({ status }: { status: CoverageGapStatus }) {
  if (status === 'open') return <Badge variant="destructive">open</Badge>;
  if (status === 'triaged') return <Badge variant="warning">triaged</Badge>;
  if (status === 'taxonomy_extension_proposed')
    return <Badge variant="secondary">proposed</Badge>;
  if (status === 'resolved') return <Badge variant="success">resolved</Badge>;
  return <Badge variant="muted">{status}</Badge>;
}

export function CoverageGapsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<StatusFilter>('open');
  const [category, setCategory] = React.useState('');

  const filters = React.useMemo(
    () => ({
      status: status || undefined,
      category: category || undefined,
      limit: 100,
    }),
    [status, category],
  );

  const q = useQuery({
    queryKey: ['ai-coverage-gaps', filters],
    queryFn: () => funnelObservabilityApi.listCoverageGaps(filters),
    staleTime: 30_000,
  });

  const columns = React.useMemo<ColumnDef<CoverageGap>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {new Date(row.original.createdAt).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) => <code className="text-xs">{row.original.category}</code>,
      },
      {
        id: 'goal',
        header: 'Goal',
        cell: ({ row }) => (
          <div className="min-w-0 max-w-md">
            <div className="line-clamp-1 font-medium">{row.original.goalText}</div>
            <div className="line-clamp-1 text-xs text-muted-foreground">
              {row.original.composerReason}
            </div>
          </div>
        ),
      },
      {
        id: 'flags',
        header: '',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.freeCompositionUsed && (
              <Badge variant="secondary" className="text-[10px]">free-comp</Badge>
            )}
            {row.original.coherenceScore !== undefined && (
              <Badge variant="outline" className="text-[10px]">
                coherence {row.original.coherenceScore.toFixed(1)}
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px]">
              {row.original.candidateApproaches.length} candidates
            </Badge>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Coverage gaps"
        description="Goals where the Composer found no taxonomy match. Triage to feed taxonomy extensions."
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1.5">
            <Label>Status</Label>
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((f) => (
                <Button
                  key={f.value || 'all'}
                  size="sm"
                  variant={status === f.value ? 'default' : 'outline'}
                  onClick={() => setStatus(f.value)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="category-filter">Category</Label>
            <Input
              id="category-filter"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. fitness"
              className="w-48"
            />
          </div>
        </CardContent>
      </Card>

      {q.isError ? (
        <ErrorState title="Coverage gaps failed" description={humanizeError(q.error)} />
      ) : (
        <DataTable
          columns={columns}
          data={q.data?.gaps ?? []}
          loading={q.isLoading}
          rowKey={(g) => g.gapId}
          onRowClick={(g) =>
            navigate(`/ai/coverage-gaps/${encodeURIComponent(g.gapId)}`)
          }
          emptyLabel="No coverage gaps"
          emptyDescription="Nothing matches the current filter set."
        />
      )}
    </div>
  );
}
